/**
 * The DOM half of the money flights: the coin in the air, the pill it lands in (the results screen's own,
 * where there is no top bar), and the storage that remembers which payouts have been shown. The logic is in
 * `money-flight.ts`; this file only touches the page.
 *
 * Blockari's `shell/money.ts` `flyCoin`, ported: the source is cloned onto `document.body` as a fixed
 * `.w21-coin-fly` box and moved with the Web Animations API along a lifted arc — keyframes at 0, 0.5
 * (the arc) and 1, `COIN_FLY_MS`, `cubic-bezier(0.65, 0, 0.35, 1)`; the source dims to a 0.25 ghost that
 * holds its slot, so there is never two coins. A stream's coins have no source to clone: they are made
 * from the coin art and start at a point on the `+N $CHAIN` figure.
 */

import { playCoinLand } from '@/ceremony/sound';
import { BalanceDisplay } from '@/ui/balance-display';
import {
  COIN_FLY_MS,
  PILL_PULSE_MS,
  createFlights,
  type Anchor,
  type FlightDeps,
  type QualityTier,
} from '@/ui/money-flight';
import { coin } from '@/ui/screens';

/** How far above the straight line the flight's mid-point sits. */
const COIN_ARC_PX = 60;
/** The source coin's opacity while its clone is in flight. */
const COIN_GHOST_ALPHA = 0.25;
/** The results screen's pill fades in before the first coin leaves (its CSS transition is 220 ms). */
export const PILL_IN_MS = 200;

export function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isElement(a: Anchor): a is Element {
  return typeof (a as Element).getBoundingClientRect === 'function';
}

function rectOf(a: Anchor): { left: number; top: number; width: number; height: number } {
  if (isElement(a)) {
    const r = a.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
  }
  return { left: a.x - a.size / 2, top: a.y - a.size / 2, width: a.size, height: a.size };
}

const arcOf = (dx: number, dy: number, scale: number, via?: { mx: number; my: number }): Keyframe[] => [
  { transform: 'translate(0, 0) scale(1)' },
  {
    // Through the waypoint when there is one; otherwise Blockari's lifted midpoint.
    transform: `translate(${via ? via.mx : dx * 0.5}px, ${via ? via.my : dy * 0.5 - COIN_ARC_PX}px) scale(${(1 + scale) / 2})`,
  },
  { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
];

/**
 * One coin from `from` to `to`. Resolves when it lands — at once if either end is missing or has no
 * size, or under reduced motion.
 */
export function flyCoin(
  from: Anchor,
  to: Anchor,
  options: { ghost?: boolean; via?: { x: number; y: number } } = {},
): Promise<void> {
  if (reducedMotion() || typeof document === 'undefined') return Promise.resolve();
  const a = rectOf(from);
  const b = rectOf(to);
  if (a.width === 0 || b.width === 0) return Promise.resolve();

  // A stream's coin has no source to clone: it is the coin art.
  const clone = isElement(from) ? (from.cloneNode(true) as HTMLElement) : coin();
  clone.classList.add('w21-coin-fly');
  clone.setAttribute('aria-hidden', 'true');
  clone.style.left = `${a.left}px`;
  clone.style.top = `${a.top}px`;
  clone.style.width = `${a.width}px`;
  clone.style.height = `${a.height}px`;
  document.body.appendChild(clone);

  const source = options.ghost && isElement(from) ? (from as HTMLElement | SVGElement) : null;
  const sourceOpacity = source?.style.opacity ?? '';
  if (source) source.style.opacity = String(COIN_GHOST_ALPHA);

  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  const via = options.via
    ? { mx: options.via.x - (a.left + a.width / 2), my: options.via.y - (a.top + a.height / 2) }
    : undefined;
  const anim = clone.animate(arcOf(dx, dy, b.width / a.width, via), {
    duration: COIN_FLY_MS,
    easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
    fill: 'forwards',
  });
  return new Promise((resolve) => {
    const done = (): void => {
      clone.remove();
      if (source) source.style.opacity = sourceOpacity;
      resolve();
    };
    anim.addEventListener('finish', done, { once: true });
    anim.addEventListener('cancel', done, { once: true });
    // A hidden tab may not run the animation to its end: never hold a stake or a payout hostage to it.
    setTimeout(done, COIN_FLY_MS + 500);
  });
}

/** The pill takes the coin: one scale pulse and an amber lift (the kit's `.w21-balance.w21-pulse`). */
export function pulse(target: Element | null): void {
  if (!target || reducedMotion()) return;
  target.classList.remove('w21-pulse');
  // Restart the animation when it is already running.
  void (target as HTMLElement).offsetWidth;
  target.classList.add('w21-pulse');
  setTimeout(() => target.classList.remove('w21-pulse'), PILL_PULSE_MS + 40);
}

/** The box of an element's text alone (a block element's own box spans its whole row). */
export function textBox(el: Element): { left: number; right: number; top: number; bottom: number } | null {
  if (typeof document === 'undefined') return null;
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    const r = range.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? { left: r.left, right: r.right, top: r.top, bottom: r.bottom } : null;
  } catch {
    return null;
  }
}

/** The real balance pill's coin on the screen the player is looking at, or null. */
export function visiblePillCoin(): Element | null {
  if (typeof document === 'undefined') return null;
  const coins = document.querySelectorAll<HTMLElement>(
    '.w21-screen[data-active="true"] .w21-topbar:not([hidden]) .w21-balance .w21-coin',
  );
  for (const c of coins) {
    const r = c.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return c;
  }
  return null;
}

/* ------------------------------------------------------------ the results pill -- */

/**
 * The pill on the results screen (`GameOverScreen`'s `.w21-flight-pill-slot`, Blockari's `ResultsPill`): the same
 * markup and classes as the top bar's, laid out for a challenge's whole results screen and unseen until a payout
 * is in the air. It fades in before the first coin leaves and stays; its number is painted with the bars'.
 */
export function createResultsPill(options: { show: () => boolean; coin: () => Element | null }) {
  return {
    async show(): Promise<void> {
      // Already in (a second payout on the same screen): nothing to wait for.
      if (options.show()) await new Promise<void>((r) => setTimeout(r, PILL_IN_MS));
    },
    coin: options.coin,
  };
}

/* ------------------------------------------------------------------- storage -- */

const SEEN_KEY = 'w21.flights.seen';
const SEEN_MAX = 200;

/** Payout ids already shown: this session, and (best effort) this browser. Storage failing costs only the memory. */
export function createSeenStore() {
  const session = new Set<string>();
  const read = (): string[] => {
    try {
      const raw = globalThis.localStorage?.getItem(SEEN_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  };
  return {
    has(id: string): boolean {
      return session.has(id) || read().includes(id);
    },
    add(id: string): void {
      session.add(id);
      try {
        const next = [...read().filter((x) => x !== id), id].slice(-SEEN_MAX);
        globalThis.localStorage?.setItem(SEEN_KEY, JSON.stringify(next));
      } catch {
        /* blocked or full: this session still remembers */
      }
    },
  };
}

/* ---------------------------------------------------------------------- wiring -- */

/** Ease-in-out, for the count. */
const ease = (t: number): number => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

export function tween(ms: number, frame: (t: number) => void): Promise<void> {
  // A hidden tab runs no animation frames: finish at once rather than never.
  if (reducedMotion() || typeof requestAnimationFrame !== 'function' || document.hidden) {
    frame(1);
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const started = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - started) / ms);
      frame(ease(t));
      if (t < 1) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });
}

/**
 * The flights for the running page: a `BalanceDisplay` that paints every top bar and the results pill, and the
 * flights over it. `paintBars` is the UI's (each screen carries its own bar, and the results screen its pill);
 * `results` is that pill's way in and its coin.
 */
export function createMoney(options: {
  paintBars: (n: number) => void;
  tier: () => QualityTier;
  results: { show: () => boolean; coin: () => Element | null };
}) {
  const resultsPill = createResultsPill(options.results);
  const display = new BalanceDisplay(options.paintBars);
  const deps: FlightDeps = {
    display,
    reduced: reducedMotion,
    tier: options.tier,
    fly: flyCoin,
    pillCoin: visiblePillCoin,
    pulse,
    resultsPill,
    textBox,
    land: (step, total) => playCoinLand(step, total),
    seen: createSeenStore(),
    sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
    tween,
  };
  return { display, flights: createFlights(deps) };
}

export type Money = ReturnType<typeof createMoney>;
